import Immutable from "immutable";
import React from "react";
import { LocalDate } from "@js-joda/core";
import { useCurrentStateAndParams, useRouter } from "@uirouter/react";
import { z } from "zod";

import { QueryParams } from "../../../../shared/hooks/useQueryParams";
import {
  CaregiverId,
  ContractTypeId,
  OfficeId,
  PatientDocumentTypeId,
  PatientId,
  PatientTaskInstanceId,
  PatientTaskTemplateId,
} from "../../../../shared/schema/schema";

export type PatientTaskNewStatus = "COMPLETED" | "INCOMPLETED" | "APPROVED";

export type TasksV2Filters = {
  scheduledFrom: LocalDate | null;
  scheduledTo: LocalDate | null;
  statuses: PatientTaskNewStatus[];
  taskTemplateIds: PatientTaskTemplateId[];
  caregiverIds: CaregiverId[];
  patientIds: PatientId[];
  contractTypeIds: ContractTypeId[];
  officeIds: OfficeId[];
  page: number;
  pageSize: number;
  sortDirection: "ASC" | "DESC";
};

export const TASKS_V2_STATE = "app.patients.careAndTaskManagement.tasksV2";

const DEFAULT_FILTERS: TasksV2Filters = {
  scheduledFrom: null,
  scheduledTo: null,
  statuses: [],
  taskTemplateIds: [],
  caregiverIds: [],
  patientIds: [],
  contractTypeIds: [],
  officeIds: [],
  page: 1,
  pageSize: 25,
  sortDirection: "DESC",
};

const FILTER_KEYS = Object.keys(DEFAULT_FILTERS) as (keyof TasksV2Filters)[];

// Remembers the last filters for the session, so leaving the tab and coming back (which lands on
// the bare URL) shows what was there before. A URL that carries filters always wins over it.
const STORAGE_KEY = JSON.stringify(["tasksV2Filters"]);

/**
 * The filters as the URL carries them (see the tasksV2 state in config.js).
 *
 * Lenient on purpose: a link someone edited by hand, or one from an older version of the page,
 * drops the values it cannot read instead of breaking the page.
 */
const zIdList = z
  .array(z.unknown())
  .nullish()
  .catch(null)
  .transform((values) => (values ?? []).map(Number).filter((id) => Number.isInteger(id) && id > 0));

const zUrlFilters = z.object({
  scheduledFrom: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullish()
    .catch(null)
    .transform((text) => (text ? LocalDate.parse(text) : null)),
  scheduledTo: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullish()
    .catch(null)
    .transform((text) => (text ? LocalDate.parse(text) : null)),
  statuses: z
    .array(z.string())
    .nullish()
    .catch(null)
    .transform((values) =>
      (values ?? []).filter((value): value is PatientTaskNewStatus =>
        ["APPROVED", "COMPLETED", "INCOMPLETED"].includes(value)
      )
    ),
  taskTemplateIds: zIdList.transform((ids) => ids.map((id) => PatientTaskTemplateId.parse(id))),
  caregiverIds: zIdList.transform((ids) => ids.map((id) => CaregiverId.parse(id))),
  patientIds: zIdList.transform((ids) => ids.map((id) => PatientId.parse(id))),
  contractTypeIds: zIdList.transform((ids) => ids.map((id) => ContractTypeId.parse(id))),
  officeIds: zIdList.transform((ids) => ids.map((id) => OfficeId.parse(id))),
  page: z.coerce
    .number()
    .int()
    .positive()
    .nullish()
    .catch(null)
    .transform((page) => page ?? DEFAULT_FILTERS.page),
  pageSize: z.coerce
    .number()
    .int()
    .positive()
    .nullish()
    .catch(null)
    .transform((size) => size ?? DEFAULT_FILTERS.pageSize),
  sortDirection: z
    .enum(["ASC", "DESC"])
    .nullish()
    .catch(null)
    .transform((direction) => direction ?? DEFAULT_FILTERS.sortDirection),
});

const zOpenTaskParams = z.object({
  taskInstanceId: z.coerce.number().int().positive().nullish().catch(null),
  // The document under review in the full-screen review modal (only meaningful with a task open).
  reviewDocumentTypeId: z.coerce.number().int().positive().nullish().catch(null),
});

// Strings only: the state declares its params untyped (see config.js), and ui-router refuses a
// number for a string param.
type UrlFilterParams = { [K in keyof TasksV2Filters]: string | string[] | null };

/** Null for anything at its default, so the URL only carries what was actually chosen. */
function toUrlParams(filters: TasksV2Filters): UrlFilterParams {
  const list = (values: (string | number)[]) => (values.length > 0 ? values.map(String) : null);

  return {
    scheduledFrom: filters.scheduledFrom?.toString() ?? null,
    scheduledTo: filters.scheduledTo?.toString() ?? null,
    statuses: list(filters.statuses),
    taskTemplateIds: list(filters.taskTemplateIds),
    caregiverIds: list(filters.caregiverIds),
    patientIds: list(filters.patientIds),
    contractTypeIds: list(filters.contractTypeIds),
    officeIds: list(filters.officeIds),
    page: filters.page === DEFAULT_FILTERS.page ? null : String(filters.page),
    pageSize: filters.pageSize === DEFAULT_FILTERS.pageSize ? null : String(filters.pageSize),
    sortDirection:
      filters.sortDirection === DEFAULT_FILTERS.sortDirection ? null : filters.sortDirection,
  };
}

function hasAnyFilter(params: Record<string, unknown>): boolean {
  return FILTER_KEYS.some((key) => {
    const value = params[key];
    return value !== null && value !== undefined && !(Array.isArray(value) && value.length === 0);
  });
}

function readStoredParams(): Record<string, unknown> | null {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    return stored === null ? null : JSON.parse(stored);
  } catch {
    return null;
  }
}

function writeStoredParams(params: UrlFilterParams | null) {
  try {
    if (params === null) {
      sessionStorage.removeItem(STORAGE_KEY);
    } else {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(params));
    }
  } catch {
    // Storage is a convenience; the URL is what holds the filters.
  }
}

type TasksV2FiltersContextValue = {
  filters: QueryParams<TasksV2Filters>;
  reviewDocumentTypeId: PatientDocumentTypeId | null;
  taskInstanceId: PatientTaskInstanceId | null;
  closeReview: () => void;
  closeTask: () => void;
  openReview: (documentTypeId: PatientDocumentTypeId) => void;
  openTask: (taskInstanceId: PatientTaskInstanceId) => void;
};

const TasksV2FiltersContext = React.createContext<TasksV2FiltersContextValue | null>(null);

export function TasksV2FiltersProvider({ children }: { children: React.ReactNode }) {
  const { params } = useCurrentStateAndParams();
  const { stateService } = useRouter();

  // The bare URL falls back to what this session had last, then to the defaults.
  const urlHasFilters = hasAnyFilter(params);
  const source = urlHasFilters ? params : readStoredParams() ?? {};
  const values = zUrlFilters.parse(source);
  const { taskInstanceId, reviewDocumentTypeId } = zOpenTaskParams.parse(params);

  const valuesRef = React.useRef(values);
  valuesRef.current = values;

  // Put restored filters into the URL too, so copying the address shares what is on screen.
  React.useEffect(() => {
    if (!urlHasFilters && hasAnyFilter(source)) {
      stateService.go(TASKS_V2_STATE, toUrlParams(values), { location: "replace" });
    }
    // Only when arriving: afterwards every change goes through the URL already.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filters = React.useMemo((): QueryParams<TasksV2Filters> => {
    function setValues(changes: Partial<TasksV2Filters>) {
      const next = { ...valuesRef.current, ...changes };
      valuesRef.current = next;
      const urlParams = toUrlParams(next);
      writeStoredParams(urlParams);
      // Replace rather than push: a history entry per filter click makes back unusable.
      stateService.go(TASKS_V2_STATE, urlParams, { location: "replace" });
    }

    return {
      params: Immutable.Map<keyof TasksV2Filters, unknown>(
        FILTER_KEYS.map((key) => [key, values[key]])
      ),
      getIsDirty: (key) =>
        JSON.stringify(toUrlParams(values)[key]) !==
        JSON.stringify(toUrlParams(DEFAULT_FILTERS)[key]),
      getValue: (key) => values[key],
      getValueOrNull: (key) => (values[key] ?? null) as NonNullable<TasksV2Filters[typeof key]>,
      setValue: (key, value) => setValues({ [key]: value }),
      setValues,
      resetToDefault: () => {
        valuesRef.current = DEFAULT_FILTERS;
        writeStoredParams(null);
        stateService.go(TASKS_V2_STATE, toUrlParams(DEFAULT_FILTERS), { location: "replace" });
      },
    };
    // `values` is rebuilt every render; the serialized form is what says it changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(toUrlParams(values)), stateService]);

  const contextValue = React.useMemo(
    (): TasksV2FiltersContextValue => ({
      filters,
      taskInstanceId: taskInstanceId ? PatientTaskInstanceId.parse(taskInstanceId) : null,
      reviewDocumentTypeId: reviewDocumentTypeId
        ? PatientDocumentTypeId.parse(reviewDocumentTypeId)
        : null,
      // Pushed, so the browser's back button closes the task (or the review) again.
      openTask: (id) =>
        stateService.go(TASKS_V2_STATE, {
          taskInstanceId: String(id),
          reviewDocumentTypeId: null,
        }),
      closeTask: () =>
        stateService.go(TASKS_V2_STATE, { taskInstanceId: null, reviewDocumentTypeId: null }),
      openReview: (documentTypeId) =>
        stateService.go(TASKS_V2_STATE, { reviewDocumentTypeId: String(documentTypeId) }),
      closeReview: () => stateService.go(TASKS_V2_STATE, { reviewDocumentTypeId: null }),
    }),
    [filters, taskInstanceId, reviewDocumentTypeId, stateService]
  );

  return (
    <TasksV2FiltersContext.Provider value={contextValue}>{children}</TasksV2FiltersContext.Provider>
  );
}

function useTasksV2Context(): TasksV2FiltersContextValue {
  const ctx = React.useContext(TasksV2FiltersContext);
  if (ctx === null) {
    throw new Error("useTasksV2Filters must be used inside a TasksV2FiltersProvider");
  }
  return ctx;
}

export function useTasksV2Filters(): QueryParams<TasksV2Filters> {
  return useTasksV2Context().filters;
}

/** The task whose documents are open in the side drawer, also held in the URL. */
export function useTasksV2OpenTask() {
  const { taskInstanceId, openTask, closeTask } = useTasksV2Context();
  return { taskInstanceId, openTask, closeTask };
}

/** The task document open in the full-screen review modal (over the drawer), held in the URL. */
export function useTasksV2OpenReview() {
  const { reviewDocumentTypeId, openReview, closeReview } = useTasksV2Context();
  return { reviewDocumentTypeId, openReview, closeReview };
}
